import type { Task, TaskStatus } from "@shared/types";

// A left→right flow of workflow stages, each listing its tasks with a
// status-colored dot. Stages are ordered by the lowest task `sequence`.
const STATUS_DOT: Record<TaskStatus, string> = {
  todo: "bg-ink-400",
  in_progress: "bg-accent",
  review: "bg-warn",
  done: "bg-positive",
};
const STATUS_LABEL: Record<TaskStatus, string> = {
  todo: "To do",
  in_progress: "In progress",
  review: "Review",
  done: "Done",
};

export default function WorkflowDiagram({ tasks }: { tasks: Task[] }) {
  const map = new Map<string, { stage: string; order: number; tasks: Task[] }>();
  for (const t of tasks) {
    const stage = t.workflowStage?.trim() || "Unstaged";
    const seq = t.sequence ?? Number.MAX_SAFE_INTEGER;
    const e = map.get(stage) ?? { stage, order: seq, tasks: [] };
    e.order = Math.min(e.order, seq);
    e.tasks.push(t);
    map.set(stage, e);
  }
  const stages = [...map.values()].sort((a, b) => a.order - b.order || a.stage.localeCompare(b.stage));
  if (stages.length === 0) return null;

  return (
    <div className="flex items-stretch gap-2 overflow-x-auto pb-3">
      {stages.map((s, i) => {
        const done = s.tasks.filter((t) => t.status === "done").length;
        return (
          <div key={s.stage} className="flex items-center gap-2">
            <div className="flex w-64 shrink-0 flex-col rounded-xl border border-ink-200 bg-white shadow-card">
              <div className="border-b border-ink-100 px-3 py-2">
                <div className="text-sm font-semibold text-ink-800">{s.stage}</div>
                <div className="text-xs text-ink-400">
                  {done}/{s.tasks.length} done
                </div>
              </div>
              <div className="space-y-1.5 p-2">
                {s.tasks.map((t) => (
                  <div key={t.id} className="rounded-lg border border-ink-100 bg-ink-50/60 px-2.5 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT[t.status]}`}
                        title={STATUS_LABEL[t.status]}
                      />
                      <span className="truncate text-xs font-medium text-ink-800">{t.title}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5 pl-3.5 text-[10px] text-ink-400">
                      <span className="truncate">{t.assigneeLabel ?? "Unassigned"}</span>
                      {t.recurrence !== "once" && (
                        <span className="rounded bg-accent-50 px-1 font-medium capitalize text-accent-700">{t.recurrence}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {i < stages.length - 1 && <div className="text-lg text-ink-300">→</div>}
          </div>
        );
      })}
    </div>
  );
}
